import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { deleteGroup, excludeGroup, readAccessList } from "../../../api/access/accessApi";
import { useAccessChanges, GroupScreen, groupNamer } from "../../../features/access";
import { useLoad } from "../../../shared/loading/useLoad";
import { appPaths } from "../../../shared/routing/appPaths";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import ConfirmAction from "../../../ui/overlays/ConfirmAction";
import { useProjectSettings } from "../projectSettingsContext";

/**
 * Project › Access › a group (redesign R3-3 `group`, `included`): one of the project's groups,
 * or an installation group it includes, on one screen.
 */
export default function ProjectGroupPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { groupId = "" } = useParams();
  const { overview, permissionsChanged } = useProjectSettings();
  const projectId = overview.project.projectId;
  const load = useCallback((signal: AbortSignal) => readAccessList(projectId, signal), [projectId]);
  const { state, reload, refresh } = useLoad(load);
  const changed = useCallback(() => { refresh(); permissionsChanged(); }, [refresh, permissionsChanged]);
  const changes = useAccessChanges(changed, state);
  const [removing, setRemoving] = useState(false);

  if (state.status !== "loaded") return <LoadStatus state={state} loadingLabel={t("common.loading")} onRetry={reload} />;
  const list = state.value;
  const namer = groupNamer(t, list);
  const group = list.groups.find((listed) => listed.groupId === groupId);
  const included = group?.projectId === null;
  const back = appPaths.projectSettingsAccess(projectId);
  const removable = group !== undefined && group.manageable && group.system === null;

  return (
    <>
      <GroupScreen
        list={list}
        groupId={groupId}
        projectName={overview.project.name}
        namer={namer}
        changes={changes}
        back={{ path: back, label: t("projects.settings.tabs.access") }}
        removal={removable ? { label: t(included ? "access.removeHere" : "access.delete"), run: () => { setRemoving(true); } } : null}
      />
      {changes.feedback}
      {removing && group !== undefined ? (
        <ConfirmAction
          title={t(included ? "access.removeHereTitle" : "access.deleteTitle", { group: namer.name(group.groupId) })}
          confirmLabel={t(included ? "access.removeHere" : "access.delete")}
          tone="danger"
          onConfirm={async () => {
            await (included ? excludeGroup(projectId, group.groupId) : deleteGroup(group.groupId));
            permissionsChanged();
            void navigate(back);
          }}
          onClose={() => { setRemoving(false); }}
        >
          <p>{t(included ? "access.removeHereExplained" : "access.deleteExplained")}</p>
        </ConfirmAction>
      ) : null}
    </>
  );
}
